import { useId } from 'react'

import {
  INTENSITY_LABELS,
  NUMERIC_FIELDS,
  WHEN_AGO_LABELS,
  type DraftField,
  type JournalIntensity,
  type JournalWhenAgo,
  type SelfReportDraft,
} from '../../lib/selfReport'
import { NumberStepper } from '../ui'
import { ChoiceChips } from './ChoiceChips'

const INTENSITY_OPTIONS = (Object.keys(INTENSITY_LABELS) as JournalIntensity[]).map((value) => ({
  value,
  label: INTENSITY_LABELS[value],
}))
const EXERCISED_OPTIONS = [
  { value: 'yes', label: 'Yes' },
  { value: 'no', label: 'No' },
] as const
const FINISHED_OPTIONS = (Object.keys(WHEN_AGO_LABELS) as JournalWhenAgo[]).map((value) => ({
  value,
  label: WHEN_AGO_LABELS[value],
}))

/**
 * ── A TOTAL, INJECTIVE CODEC, AND WHY IT IS WRITTEN OUT ─────────────────────
 *
 * `exercised` is a boolean where **false is an answer** and `null` means nobody
 * said. Everybody writes the first half of that rule down, because it protects
 * *writing* `false`. The converse goes unwritten: never INVENT `false`. A chip
 * control compares `value === option.value`, so any encoding that lets `null`
 * collapse towards `'no'` renders "No" as pressed for a question nobody answered
 * -- a fabricated answer, on screen, in the one place the user is meant to be the
 * only author.
 */
const exercisedToChoice = (value: boolean | null): 'yes' | 'no' | null =>
  value === true ? 'yes' : value === false ? 'no' : null
const choiceToExercised = (choice: 'yes' | 'no' | null): boolean | null =>
  choice === 'yes' ? true : choice === 'no' ? false : null

const MINUTES = NUMERIC_FIELDS.exercise_minutes

interface ExerciseFieldsProps {
  draft: SelfReportDraft
  set: <K extends DraftField>(field: K, value: SelfReportDraft[K]) => void
  minutesError: string | null
}

/**
 * The Movement block, shared by BOTH journal forms: the recording page's
 * `SelfReportForm` and the end-of-recording `CaptureJournal` (JOURNAL-FIX-02).
 *
 * One component rather than two copies, because the copies drifted: the lock
 * below was added to one form and the other kept accepting "No, lightly, for 20
 * minutes", which then travelled inside the recording as `journal.json`.
 *
 * JOURNAL-FIX-01 (option A). "Exercised: No" locks Intensity and Minutes:
 * empty, they are disabled, so the contradiction cannot be entered; already
 * holding a value, they stay on screen with the note below and can only be
 * cleared. Nothing is cleared for the user.
 *
 * Sprint 1.9.11 adds **Finished** under the same lock rather than beside it.
 * All three describe one exercise session, so "No, moderate, 30 min, finished an
 * hour ago" is the same contradiction in four fields instead of three — and it
 * is the field the whole schema bump exists for, since without timing a daily
 * `exercised` flag cannot be read as pre-recording exposure (BSV-011 §1).
 * Extending the existing lock, never forking it, is also why this component is
 * shared: the copies drifted once, and the end-of-recording form went on
 * accepting "No, lightly, for 20 minutes" into the archive.
 *
 * Returns the grid of four controls, then the note's live region.
 */
export function ExerciseFields({ draft, set, minutesError }: ExerciseFieldsProps) {
  const locked = draft.exercised === false
  const noteId = useId()
  const minutesClearOnly = locked && draft.exercise_minutes !== ''
  // Every locked field counts, so the note cannot go missing while one of them
  // still holds an answer. It was `intensity || minutes`; a fourth control added
  // without this line is a value nobody is told about.
  const held =
    draft.exercise_finished !== null
    || draft.exercise_intensity !== null
    || draft.exercise_minutes !== ''
  return (
    <>
      <div className="journal__group journal__group--grid">
        <ChoiceChips
          label="Exercised"
          options={EXERCISED_OPTIONS}
          value={exercisedToChoice(draft.exercised)}
          onChange={(next) => set('exercised', choiceToExercised(next))}
        />
        <ChoiceChips
          label="Finished"
          options={FINISHED_OPTIONS}
          value={draft.exercise_finished}
          onChange={(next) => set('exercise_finished', next as JournalWhenAgo | null)}
          disabled={locked && draft.exercise_finished === null}
          clearOnly={locked && draft.exercise_finished !== null}
        />
        <ChoiceChips
          label="Intensity"
          options={INTENSITY_OPTIONS}
          value={draft.exercise_intensity}
          onChange={(next) => set('exercise_intensity', next as JournalIntensity | null)}
          disabled={locked && draft.exercise_intensity === null}
          clearOnly={locked && draft.exercise_intensity !== null}
        />
        <NumberStepper
          disabled={locked && draft.exercise_minutes === ''}
          clearOnly={minutesClearOnly}
          // Only while Minutes is clear-only. In that state the note is always on
          // screen (its condition includes a held duration), so the id never
          // points at nothing.
          describedBy={minutesClearOnly ? noteId : undefined}
          label={MINUTES.label}
          value={draft.exercise_minutes}
          onChange={(next) => set('exercise_minutes', next)}
          min={MINUTES.min}
          max={MINUTES.max}
          step={MINUTES.step}
          unit={MINUTES.unit}
          error={minutesError}
        />
      </div>
      {/*
        An always-mounted live region, empty until "No" meets a held intensity or
        duration: a region inserted together with its content is announced
        unreliably (see src/test/liveRegions.test.ts). OUTSIDE the grid on
        purpose: an empty grid item still costs a grid gap, and outside it the
        empty wrapper takes no space. Never `display: none`, which would take the
        region out of the accessibility tree.
      */}
      <div className="journal__note-region" role="status">
        {locked && held && (
          /*
            NOT auto-cleared. Hiding or emptying a control that holds a stored
            value is deleting an answer the user did not withdraw.
          */
          <p className="journal__note" id={noteId}>
            There is still a finishing time, an intensity or a duration recorded here. Clear
            them yourself if they are wrong.
          </p>
        )}
      </div>
    </>
  )
}
